import { useI18n } from '@affine/i18n';
import { type FC, useMemo } from 'react';

import { resolveBindings } from '../../../services/capability-matcher/capability-matcher';
import type { DbColumnInfo } from '../../../services/capability-matcher/types';
import type { ComponentCapability } from '../../../types/capability-schema';
import type { ContentDbColumn } from '../utils/get-content-db-columns';
import * as css from './bindings.css';
import { BindingRow } from './bindings-row';
import type { SectionProps } from './types';

interface BindingsSectionProps extends SectionProps {
  columns: ReadonlyArray<ContentDbColumn>;
  activeCapabilities: ReadonlyArray<ComponentCapability>;
}

function toDbColumnInfo(cols: ReadonlyArray<ContentDbColumn>): DbColumnInfo[] {
  return cols.map(c => ({ id: c.id, name: c.name, type: c.type }));
}

export const BindingsSection: FC<BindingsSectionProps> = ({
  row,
  setField,
  columns,
  activeCapabilities,
}) => {
  const t = useI18n();
  const result = useMemo(
    () =>
      resolveBindings({
        capabilities: activeCapabilities as ComponentCapability[],
        columns: toDbColumnInfo(columns),
        overrides: row.bindings,
      }),
    [activeCapabilities, columns, row.bindings]
  );

  if (activeCapabilities.length === 0) {
    return (
      <p className={css.emptyNote} data-sl-role="bindings-empty">
        {t['com.streamlain.customApps.viewConfig.bindings.empty']()}
      </p>
    );
  }

  const missingSet = new Set(result.missingRequired);

  const writeBinding = (key: string, newColumnId: string | null): void => {
    const next: Record<string, string> = { ...row.bindings };
    if (newColumnId === null) {
      delete next[key];
    } else {
      next[key] = newColumnId;
    }
    setField('bindings', next);
  };

  return (
    <div className={css.bindingsList} data-sl-role="bindings-list">
      {activeCapabilities.flatMap(cap =>
        cap.requirements.map(req => {
          const key = `${cap.role}:${req.field}`;
          const binding = result.bindings[key];
          return (
            <BindingRow
              key={key}
              role={cap.role}
              field={req.field}
              required={req.required}
              current={binding?.columnId ?? null}
              compatibility={binding?.compatibility ?? 'incompatible'}
              source={binding?.source ?? 'unresolved'}
              columns={columns}
              acceptedTypes={req.acceptedTypes}
              missing={missingSet.has(key)}
              onChange={columnId => writeBinding(key, columnId)}
            />
          );
        })
      )}
    </div>
  );
};
